import { equal, fromNumber } from 'ezmoney';
import { type FormikErrors, yupToFormErrors, validateYupSchema } from 'formik';
import * as yup from 'yup';

import { filterOutUndefinedProperties } from 'common/utils/filterOutUndefinedProperties';
import * as Schemas from 'common/utils/forms/schemas';

import type * as BudgetTrackingTrait from './budgetTrackingTrait';
import * as CustomFieldAssociation from '../customFieldAssociation';

export type ValidationContext = {
  isCostCenterRequired: boolean;
  isTeamRequired: boolean;
  requiredCustomFields?: string[];
  totalAmount?: number; // this will be used to validate that analytical split items sum up to total amount
};

export type CustomFieldAssociationError = {
  [customFieldId: string]: Schemas.RequiredError;
};

export type AnalyticalSplitItemError = {
  amount?: string;
  costCenterId?: Schemas.RequiredError;
  customFields?: CustomFieldAssociationError;
};
export type AnalyticalSplitError = {
  dimensions?: Schemas.RequiredError; // ensures that at least one dimension is selected
  completeAllocation?: Schemas.RequiredError; // ensures that total of items amounts matches total amount
  items?: { [itemId: string]: AnalyticalSplitItemError };
};

export type Errors = {
  costCenterId?: Schemas.RequiredError;
  customFieldsAssociations?: CustomFieldAssociationError;
  teamId?: Schemas.RequiredError;
  analyticalSplit?: AnalyticalSplitError;
};

export type ToFormikErrors<T extends BudgetTrackingTrait.DraftTrait> = Omit<
  FormikErrors<T>,
  'customFieldsAssociations' | 'analyticalSplit'
> & {
  customFieldsAssociations?: CustomFieldAssociationError;
  analyticalSplit?: AnalyticalSplitError;
};

// custom type because the customField error shape is not compliant with formik
export type BudgetTrackingFormikErrors =
  ToFormikErrors<BudgetTrackingTrait.DraftTrait>;

export const validate = (
  budgetTracking: BudgetTrackingTrait.DraftTrait,
  validationContext: ValidationContext,
): BudgetTrackingFormikErrors | undefined => {
  // note: when analytical split is activated, we have a slightly different validation
  // esp we need to split custom fields between "root" and "analytical split items"
  if (budgetTracking.analyticalSplit?.activate) {
    return validateWithAnalyticalSplit(budgetTracking, validationContext);
  }

  const customFieldError = validateCustomFields(
    budgetTracking.customFieldsAssociations,
    validationContext,
  );

  try {
    validateYupSchema(budgetTracking, budgetTrackingValidationSchema, true, {
      ...validationContext,
      budgetTracking,
    });
    return customFieldError
      ? { customFieldsAssociations: customFieldError }
      : undefined;
  } catch (e) {
    return {
      ...(yupToFormErrors<BudgetTrackingTrait.DraftTrait>(
        e,
      ) as BudgetTrackingFormikErrors),
      ...(customFieldError
        ? { customFieldsAssociations: customFieldError }
        : {}),
    };
  }
};

export const validateWithAnalyticalSplit = (
  budgetTracking: BudgetTrackingTrait.DraftTrait,
  validationContext: ValidationContext,
): BudgetTrackingFormikErrors | undefined => {
  const {
    dimensions: { splitOnCostCenter = false, splitOnCustomFieldsIds = [] } = {},
    items = [],
  } = budgetTracking.analyticalSplit ?? {};

  // first validate "root" values excluding analytical split custom fields dimensions
  const rootFieldsErrors = validate(
    {
      ...budgetTracking,
      analyticalSplit: undefined, // disable AS in recursive validation
    },
    {
      isTeamRequired: validationContext.isTeamRequired,
      isCostCenterRequired:
        validationContext.isCostCenterRequired && !splitOnCostCenter,
      requiredCustomFields: validationContext.requiredCustomFields?.filter(
        (cfId) => !splitOnCustomFieldsIds.includes(cfId),
      ),
    },
  );

  // then validate analytical split items individually
  const itemsValidationContext: ValidationContext = {
    isCostCenterRequired:
      validationContext.isCostCenterRequired && splitOnCostCenter,
    // team is not required at item level
    isTeamRequired: false,
    // custom fields required are only those that are part of the split
    requiredCustomFields: validationContext.requiredCustomFields?.filter(
      (cfId) => splitOnCustomFieldsIds.includes(cfId),
    ),
  };

  const itemsErrors = items.reduce<Record<string, AnalyticalSplitItemError>>(
    (errorsAccumulator, item) => {
      const itemAmountError =
        !item.amount || Number.isNaN(item.amount)
          ? Schemas.requiredError
          : undefined;

      const itemCostCenterError =
        !item.costCenterId && itemsValidationContext.isCostCenterRequired
          ? Schemas.requiredError
          : undefined;

      const itemCustomFieldsError = validateCustomFields(
        item.customFields ?? [],
        itemsValidationContext,
      );

      if (itemAmountError || itemCostCenterError || itemCustomFieldsError) {
        errorsAccumulator[item.id] = {
          amount: itemAmountError,
          costCenterId: itemCostCenterError,
          customFields: itemCustomFieldsError,
        };
      }

      return errorsAccumulator;
    },
    {},
  );

  // finally check global consistency of AS items:
  // - at least one dimension must be selected
  const dimensionsError =
    !splitOnCostCenter && !splitOnCustomFieldsIds.length
      ? Schemas.requiredError
      : undefined;

  // - their sum must match the root level allocation
  const totalAmount = validationContext.totalAmount ?? 0;
  const totalAllocation = items.reduce(
    (sum, item) =>
      sum + (!item.amount || Number.isNaN(item.amount) ? 0 : item.amount),
    0,
  );
  const completeAllocationError =
    !Number.isNaN(totalAmount) &&
    !equal(fromNumber(totalAmount, '', 2), fromNumber(totalAllocation, '', 2)) // note: currency not needed for comparison
      ? Schemas.requiredError
      : undefined;

  const analyticalSplitErrors =
    dimensionsError ||
    completeAllocationError ||
    Object.keys(itemsErrors).length > 0
      ? {
          dimensions: dimensionsError,
          completeAllocation: completeAllocationError,
          items: Object.keys(itemsErrors).length > 0 ? itemsErrors : undefined,
        }
      : undefined;

  return rootFieldsErrors || analyticalSplitErrors
    ? {
        ...rootFieldsErrors,
        analyticalSplit: analyticalSplitErrors,
      }
    : undefined;
};

export const fromFormikErrors = (
  formikErrors: BudgetTrackingFormikErrors,
): Errors =>
  filterOutUndefinedProperties({
    costCenterId: Schemas.parseToRequiredError(formikErrors.costCenterId),
    customFieldsAssociations:
      formikErrors.customFieldsAssociations as unknown as CustomFieldAssociationError,
    teamId: Schemas.parseToRequiredError(formikErrors.teamId),
    analyticalSplit:
      formikErrors.analyticalSplit as unknown as AnalyticalSplitError,
  });

const validateCustomFields = (
  customFieldsAssociations: CustomFieldAssociation.CustomFieldAssociation[],
  { requiredCustomFields }: ValidationContext,
): Errors['customFieldsAssociations'] => {
  if (!requiredCustomFields || requiredCustomFields.length === 0) {
    return undefined;
  }

  const error = requiredCustomFields.reduce(
    (
      errorAccumulator: { [customFieldId: string]: Schemas.RequiredError },
      requiredCustomFieldId: string,
    ) => {
      const isRequiredCustomFieldDefined = customFieldsAssociations.some(
        (customFieldsAssociation) =>
          customFieldsAssociation.customFieldId === requiredCustomFieldId &&
          CustomFieldAssociation.isDefinedCustomFieldAssociation(
            customFieldsAssociation,
          ),
      );

      if (!isRequiredCustomFieldDefined) {
        errorAccumulator[requiredCustomFieldId] = Schemas.requiredError;
      }
      return errorAccumulator;
    },
    {},
  );

  return Object.keys(error).length === 0 ? undefined : error;
};

const budgetTrackingValidationSchema = yup.object().shape({
  costCenterId: yup.string().when('$isCostCenterRequired', {
    is: true,
    then: () => Schemas.text,
    otherwise: () => Schemas.optionalText,
  }),
  teamId: yup.string().when('$isTeamRequired', {
    is: true,
    then: () => Schemas.text,
    otherwise: () => Schemas.optionalText,
  }),
});
